import React, { useCallback, useEffect, useState } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../components/AuthProvider";
import { checkSessionProfileApi } from "../api/userApi";
import { CircularProgress, Box } from "@mui/material";
import { CreditsBalanceProvider } from "../context/CreditsBalanceContext";
import PricingSubscriptionGate from "../components/Pricing/PricingSubscriptionGate";
import FreemiumTrialShell from "../components/FreemiumTrial/FreemiumTrialShell";
import { isDevAuthBypassEnabled } from "../helper/auth";
import { shouldRouteToOnboarding } from "../helper/onboardingHandoff";
import { clearPricingRequirement } from "../helper/pricingHandoff";

const PrivateRoutes = () => {
  const {
    token,
    setUserEmail,
    setUserFirstName,
    setUserLastName,
    setToken,
    setRole,
  } = useAuth();
  const [isVerifying, setIsVerifying] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [profile, setProfile] = useState(null);
  const [onboardingExitStarted, setOnboardingExitStarted] = useState(false);
  const { pathname } = useLocation();
  const devAuthBypass = isDevAuthBypassEnabled();
  const isFreemium = profile?.role?.role_name === "freemium";
  const onboardingCompleted = Boolean(profile?.onboarding_completed_at);
  const hideActiveTrialStatus = pathname !== "/change-password";
  // Onboarding promises a free first run; the trial credits banner must not
  // render there in any state (owner ruling 2026-10-09).
  const hideTrialStatus = pathname === "/onboarding";
  const beginOnboardingExit = useCallback(() => {
    // Bridge only this mounted session past its stale pre-completion profile.
    // Reloads still re-read the backend-owned completion timestamp.
    setOnboardingExitStarted(true);
  }, []);

  useEffect(() => {
    // Prototype: require an in-memory token from Login (no auto-login / no API).
    if (!devAuthBypass) return;
    setIsAuthenticated(Boolean(token));
    setIsVerifying(false);
  }, [devAuthBypass, token]);

  useEffect(() => {
    if (devAuthBypass) return;
    let current = true;
    setIsVerifying(true);

    checkSessionProfileApi()
      .then(res => {
        if (!current) return;
        if (res && res.status === 200) {
          setUserFirstName(res.data.first_name);
          setUserLastName(res.data.last_name);
          setUserEmail(res.data.email);
          setRole(res.data.role ?? null);
          setProfile(res.data);
          setToken("cookie-session");
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      })
      .catch(() => {
        if (current) setIsAuthenticated(false);
      })
      .finally(() => {
        if (current) setIsVerifying(false);
      });
    return () => { current = false; };
  }, [devAuthBypass, setRole, setToken, setUserEmail, setUserFirstName, setUserLastName]);

  useEffect(() => {
    if (isAuthenticated && isFreemium) {
      clearPricingRequirement();
    }
  }, [isAuthenticated, isFreemium]);

  if (isVerifying) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (
    isAuthenticated
    && !onboardingExitStarted
    && pathname !== "/onboarding"
    && shouldRouteToOnboarding(profile)
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  if (
    isAuthenticated
    && pathname === "/onboarding"
    && (onboardingCompleted || onboardingExitStarted)
  ) {
    return <Navigate to="/lists/companies/" replace />;
  }

  return (
    <>
      {isAuthenticated ? (
        <CreditsBalanceProvider profile={profile}>
          {isFreemium ? (
            <FreemiumTrialShell profile={profile} hideActiveStatus={hideActiveTrialStatus} hideStatus={hideTrialStatus}>
              <Outlet context={{ beginOnboardingExit }} />
            </FreemiumTrialShell>
          ) : (
            <FreemiumTrialShell profile={profile} hideActiveStatus={hideActiveTrialStatus} hideStatus={hideTrialStatus}>
              <PricingSubscriptionGate>
                <Outlet context={{ beginOnboardingExit }} />
              </PricingSubscriptionGate>
            </FreemiumTrialShell>
          )}
        </CreditsBalanceProvider>
      ) : (
        <Navigate to="/" />
      )}
    </>
  );
};

export default PrivateRoutes;
